/**
 * Sync API service — wraps all /sync/* endpoints.
 */

const API_BASE_URL = '/api';

const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('token');
  const t = localStorage.getItem('currentTenant') || 'default';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  headers['X-Tenant-Code'] = t;
  return fetch(url, { ...options, headers });
};

const handleResponse = async (res: Response) => {
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const code = body?.error?.code || `HTTP_${res.status}`;
    const msg = body?.error?.message || body?.message || `Request failed (${res.status})`;
    const err: any = new Error(msg);
    err.status = res.status;
    err.code = code;
    err.details = body?.error?.details;
    throw err;
  }
  return body;
};

const tenant = () => localStorage.getItem('currentTenant') || 'default';

// ── Preview / Apply / History / Rollback ─────────────────────────────────────
export const syncService = {
  async preview(payload: any) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/${tenant()}/sync/preview`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }));
  },
  async getPreview(previewId: string, page = 1, size = 100) {
    return handleResponse(await authFetch(
      `${API_BASE_URL}/v1/${tenant()}/sync/preview/${previewId}?page=${page}&size=${size}`,
    ));
  },
  async apply(payload: { previewId: string; decisions: Array<{ key: string; action: string }>; defaultAction: string }) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/${tenant()}/sync/apply`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }));
  },
  async history(params: { page?: number; size?: number; projectId?: string } = {}, tenantCode?: string) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.size) qs.set('size', String(params.size));
    if (params.projectId) qs.set('projectId', params.projectId);
    return handleResponse(await authFetch(
      `${API_BASE_URL}/v1/${tenantCode || tenant()}/sync/history${qs.toString() ? '?' + qs.toString() : ''}`,
    ));
  },
  async rollback(syncJobId: string, tenantCode?: string) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/${tenantCode || tenant()}/sync/rollback`, {
      method: 'POST',
      body: JSON.stringify({ syncJobId }),
    }));
  },
};

// ── Base Projects ────────────────────────────────────────────────────────────
export const syncBaseService = {
  async list(tenantCode?: string) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/${tenantCode || tenant()}/sync/base`));
  },
  async listGlobal() {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/sync/base/global`));
  },
  async setBase(payload: { scope: 'tenant' | 'global'; projectId: string; entityTypes: string[]; description?: string; force?: boolean }, tenantCode?: string) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/${tenantCode || tenant()}/sync/base`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }));
  },
  async remove(id: string | number, tenantCode?: string) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/${tenantCode || tenant()}/sync/base/${id}`, {
      method: 'DELETE',
    }));
  },
};

// ── Cross-System Tokens ──────────────────────────────────────────────────────
export const syncTokenService = {
  /** List tokens. Optional direction filter ('outbound' | 'inbound'). */
  async list(direction?: 'outbound' | 'inbound') {
    const qs = direction ? `?direction=${direction}` : '';
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/sync/tokens${qs}`));
  },
  async register(payload: {
    alias: string;
    baseUrl?: string;
    token: string;
    scope: { entityTypes: string[] };
    expiresAt?: string;
    direction: 'outbound' | 'inbound';
  }) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/sync/tokens`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }));
  },
  async updateScope(alias: string, entityTypes: string[]) {
    return handleResponse(
      await authFetch(`${API_BASE_URL}/v1/sync/tokens/${encodeURIComponent(alias)}/scope`, {
        method: 'PATCH',
        body: JSON.stringify({ scope: { entityTypes } }),
      })
    );
  },
  async revoke(alias: string) {
    return handleResponse(await authFetch(`${API_BASE_URL}/v1/sync/tokens/${encodeURIComponent(alias)}`, {
      method: 'DELETE',
    }));
  },
};
